import * as React from 'react';
import Box from '@mui/material/Box';
import MuiCard from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {styled} from '@mui/material/styles';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import {PaymentsOutlined} from "@mui/icons-material";
import {Trans} from "react-i18next";
import CartManager, {useCartChange} from "../../managers/cartManager/cartManager";
import CardPayment from "./cardPayment/CardPayment";
import {useEffect} from "react";

const Card = styled(MuiCard)(({theme}) => ({
    border: '1px solid',
    borderColor: (theme.vars || theme).palette.divider,
    width: '100%',
    '&:hover': {
        background:
            'linear-gradient(to bottom right, hsla(210, 100%, 97%, 0.5) 25%, hsla(210, 100%, 90%, 0.3) 100%)',
        borderColor: 'primary.light',
        boxShadow: '0px 2px 8px hsla(0, 0%, 0%, 0.1)',
        ...theme.applyStyles('dark', {
            background:
                'linear-gradient(to right bottom, hsla(210, 100%, 12%, 0.2) 25%, hsla(210, 100%, 16%, 0.2) 100%)',
            borderColor: 'primary.dark',
            boxShadow: '0px 1px 8px hsla(210, 100%, 25%, 0.5) ',
        }),
    },
    [theme.breakpoints.up('md')]: {
        flexGrow: 1,
        maxWidth: `calc(50% - ${theme.spacing(1)})`,
    },
    variants: [
        {
            props: ({selected}) => selected,
            style: {
                borderColor: (theme.vars || theme).palette.primary.light,
                ...theme.applyStyles('dark', {
                    borderColor: (theme.vars || theme).palette.primary.dark,
                }),
            },
        },
    ],
}));

const InactiveCard = styled(MuiCard)(({theme}) => ({
    border: '1px solid',
    borderColor: (theme.vars || theme).palette.divider,
    width: '100%',
    color: (theme.vars || theme).palette.text.disabled,
    [theme.breakpoints.up('md')]: {
        flexGrow: 1,
        maxWidth: `calc(50% - ${theme.spacing(1)})`,
    },
    variants: [
        {
            props: ({selected}) => selected,
            style: {
                borderColor: (theme.vars || theme).palette.primary.light,
                ...theme.applyStyles('dark', {
                    borderColor: (theme.vars || theme).palette.primary.dark,
                }),
            },
        },
    ],
}));

export default function PaymentForm() {
    const [paymentType, setPaymentType] = React.useState(CartManager.getPaymentType() || 'creditCard');
    const cart = useCartChange();
    const shipmentType = cart.shipping.type;
    useEffect(() => {
        setPaymentType(cart?.paymentType);
    }, [cart?.shipping?.type])

    const handlePaymentChange = (type) => {
        if (cart.shipping.type !== 'personal') {
            setPaymentType(type);
            CartManager.setPaymentType(type);
        }
    }

    const Wrapper = shipmentType === 'personal' ? InactiveCard : Card;

    return (
        <Stack spacing={{xs: 3, sm: 6}} useFlexGap>
            <FormControl component="fieldset" fullWidth>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: {xs: 'column', sm: 'row'},
                        gap: 2,
                    }}
                >
                    <Card selected={paymentType === 'creditCard'}>
                        <CardActionArea
                            onClick={() => handlePaymentChange('creditCard')}
                            sx={{
                                '.MuiCardActionArea-focusHighlight': {
                                    backgroundColor: 'transparent',
                                },
                                '&:focus-visible': {
                                    backgroundColor: 'action.hover',
                                },
                            }}
                        >
                            <CardContent sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <CreditCardRoundedIcon
                                    fontSize="small"
                                    sx={[
                                        (theme) => ({
                                            color: 'grey.400',
                                            ...theme.applyStyles('dark', {
                                                color: 'grey.600',
                                            }),
                                        }),
                                        paymentType === 'creditCard' && {
                                            color: 'primary.main',
                                        },
                                    ]}
                                />
                                <Typography sx={{fontWeight: 'medium'}}><Trans
                                    i18nKey={'paymentForm.creditCard'}/></Typography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                    <Wrapper selected={paymentType === 'cash'}>
                        <CardActionArea
                            onClick={() => handlePaymentChange('cash')}
                            sx={{
                                '.MuiCardActionArea-focusHighlight': {
                                    backgroundColor: 'transparent',
                                },
                                '&:focus-visible': {
                                    backgroundColor: 'action.hover',
                                },
                                cursor: shipmentType === 'personal' && 'initial'
                            }}
                        >
                            <CardContent sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                <PaymentsOutlined
                                    fontSize="small"
                                    sx={[
                                        (theme) => ({
                                            color: 'grey.400',
                                            ...theme.applyStyles('dark', {
                                                color: 'grey.600',
                                            }),
                                        }),
                                        paymentType === 'cash' && {
                                            color: 'primary.main',
                                        },
                                    ]}
                                />
                                <Typography sx={{fontWeight: 'medium'}}><Trans
                                    i18nKey={'paymentForm.cash'}/></Typography>
                            </CardContent>
                        </CardActionArea>
                    </Wrapper>
                </Box>
            </FormControl>
            {paymentType === 'creditCard' && (
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                    <CardPayment/>
                </Box>
            )}
            {paymentType === 'cash' && (
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>

                </Box>
            )}
        </Stack>
    );
}
